/**
 * Generic Local Storage Service
 * Provides type-safe access to localStorage with JSON serialization
 */

/**
 * Gets a value from localStorage with type safety
 * @param key - The storage key
 * @param defaultValue - Default value if key doesn't exist
 * @returns The stored value or default value
 */
export function getFromStorage<T>(key: string, defaultValue: T): T {
  try {
    const item = localStorage.getItem(key);
    if (item === null) {
      return defaultValue;
    }
    return JSON.parse(item) as T;
  } catch (error) {
    console.error(`Error reading from localStorage (key: ${key}):`, error);
    return defaultValue;
  }
}

/**
 * Sets a value in localStorage with JSON serialization
 * @param key - The storage key
 * @param value - The value to store
 */
export function setInStorage<T>(key: string, value: T): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error(`Error writing to localStorage (key: ${key}):`, error);
  }
}

/**
 * Removes a value from localStorage
 * @param key - The storage key
 */
export function removeFromStorage(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error(`Error removing from localStorage (key: ${key}):`, error);
  }
}

/**
 * Clears all items from localStorage
 */
export function clearStorage(): void {
  try {
    localStorage.clear();
  } catch (error) {
    console.error('Error clearing localStorage:', error);
  }
}

/**
 * Checks if a key exists in localStorage
 * @param key - The storage key
 * @returns True if key exists
 */
export function hasKey(key: string): boolean {
  return localStorage.getItem(key) !== null;
}
